import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, Database, FileSearch, Loader2, RefreshCw } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

type DriftClassification =
  | "match"
  | "expected-superseded"
  | "real-difference"
  | "errored";

interface DifferenceCount {
  rowsDiffering: number;
  fieldsDiffering: number;
}

interface ShadowResult {
  runId: string;
  v1ReportId: string;
  location: string;
  reportDate: string;
  runAt: string;
  harnessVersion: string;
  outcome: "match" | "differ" | "errored";
  isSupersededAtShadow: boolean | null;
  laterReportDate: string | null;
  differenceCounts: Record<string, DifferenceCount | number>;
  detail: unknown;
  errorDetail: unknown;
}

interface ResultsResponse {
  ok: boolean;
  results: ShadowResult[];
}

interface DetailResponse {
  ok: boolean;
  result: ShadowResult;
}

interface DetectionCandidate {
  reportId: string;
  acceptedAt: string;
  location: string;
  reportDate: string;
}

interface DetectionResponse {
  ok: boolean;
  state: {
    stage: number;
    acceptedAtWatermark: string | null;
    scopeLocation: string | null;
    scopeStartDate: string | null;
    scopeEndDate: string | null;
    scopeCurrentOnly: boolean;
    reclaimTimeoutMinutes: number;
    lastRunAt: string | null;
  };
  candidates: DetectionCandidate[];
}

const TABLE_LABELS: Record<string, string> = {
  reports: "reports",
  report_line_items: "line items",
  pending_report_data: "pending",
  animals: "animals",
  animal_currents: "currents",
};

function classify(result: ShadowResult): DriftClassification {
  if (result.outcome === "errored") return "errored";
  if (result.outcome === "match") return "match";
  return result.isSupersededAtShadow ? "expected-superseded" : "real-difference";
}

function classificationLabel(value: DriftClassification): string {
  switch (value) {
    case "expected-superseded":
      return "Expected superseded";
    case "real-difference":
      return "Real difference";
    case "errored":
      return "Errored";
    default:
      return "Match";
  }
}

function classificationVariant(
  value: DriftClassification,
): "default" | "secondary" | "destructive" | "outline" {
  switch (value) {
    case "real-difference":
    case "errored":
      return "destructive";
    case "expected-superseded":
      return "secondary";
    case "match":
      return "outline";
  }
}

function formatTimestamp(value: string | null): string {
  if (!value) return "Never";
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? value : parsed.toLocaleString();
}

function differenceSummary(counts: ShadowResult["differenceCounts"]): string {
  const parts = Object.entries(TABLE_LABELS)
    .map(([key, label]) => {
      const value = counts?.[key];
      if (!value || typeof value === "number" || value.fieldsDiffering === 0) return null;
      return `${label}: ${value.rowsDiffering} row${value.rowsDiffering === 1 ? "" : "s"}, ${value.fieldsDiffering} field${value.fieldsDiffering === 1 ? "" : "s"}`;
    })
    .filter(Boolean);
  return parts.length > 0 ? parts.join(" · ") : "No differences";
}

function JsonDetail({ value }: { value: unknown }) {
  if (value === null || value === undefined) {
    return <p className="text-sm text-muted-foreground">No detail recorded.</p>;
  }
  return (
    <pre className="max-h-96 overflow-auto rounded-md bg-muted p-4 text-xs whitespace-pre-wrap break-words">
      {JSON.stringify(value, null, 2)}
    </pre>
  );
}

export function HubShadowResultsCard() {
  const [filter, setFilter] = useState<"all" | DriftClassification>("all");
  const [selectedRunId, setSelectedRunId] = useState<string | null>(null);

  const resultsQuery = useQuery<ResultsResponse>({
    queryKey: ["/api/admin/hub/shadow-results?limit=200"],
  });
  const detectionQuery = useQuery<DetectionResponse>({
    queryKey: ["/api/admin/hub/shadow-detection-preview"],
  });
  const detailQuery = useQuery<DetailResponse>({
    queryKey: [`/api/admin/hub/shadow-results/${selectedRunId ?? ""}`],
    enabled: selectedRunId !== null,
  });

  const filteredResults = useMemo(() => {
    const rows = resultsQuery.data?.results ?? [];
    if (filter === "all") return rows;
    return rows.filter((row) => classify(row) === filter);
  }, [filter, resultsQuery.data?.results]);

  const refresh = () => {
    void resultsQuery.refetch();
    void detectionQuery.refetch();
    if (selectedRunId) void detailQuery.refetch();
  };

  const detection = detectionQuery.data;
  const selected = detailQuery.data?.result;

  return (
    <div className="space-y-6" data-testid="hub-shadow-results">
      <Card>
        <CardHeader className="flex flex-row items-start justify-between gap-4">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Database className="h-5 w-5" />
              H0b Shadow Results
            </CardTitle>
            <CardDescription>
              Read-only Data Hub v2 comparison evidence. Rollout controls remain operator-managed.
            </CardDescription>
          </div>
          <Button variant="outline" size="sm" onClick={refresh} data-testid="button-refresh-h0b">
            <RefreshCw className="mr-2 h-4 w-4" />
            Refresh
          </Button>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm text-muted-foreground">
              Real differences are separated from expected historical supersession drift.
            </p>
            <Select value={filter} onValueChange={(value) => setFilter(value as typeof filter)}>
              <SelectTrigger className="w-52" data-testid="select-h0b-result-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All runs</SelectItem>
                <SelectItem value="real-difference">Real differences</SelectItem>
                <SelectItem value="expected-superseded">Expected superseded</SelectItem>
                <SelectItem value="errored">Errored</SelectItem>
                <SelectItem value="match">Matches</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {resultsQuery.isLoading ? (
            <div className="space-y-2">
              {[1, 2, 3].map((row) => <Skeleton key={row} className="h-12 w-full" />)}
            </div>
          ) : resultsQuery.isError ? (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>Could not load shadow results</AlertTitle>
              <AlertDescription>{String(resultsQuery.error)}</AlertDescription>
            </Alert>
          ) : (
            <div className="overflow-x-auto rounded-md border">
              <Table data-testid="table-h0b-results">
                <TableHeader>
                  <TableRow>
                    <TableHead>Classification</TableHead>
                    <TableHead>Location</TableHead>
                    <TableHead>Report date</TableHead>
                    <TableHead>Run time</TableHead>
                    <TableHead>Differences</TableHead>
                    <TableHead className="text-right">Detail</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredResults.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="py-8 text-center text-muted-foreground">
                        No runs match this filter.
                      </TableCell>
                    </TableRow>
                  ) : filteredResults.map((result) => {
                    const classification = classify(result);
                    return (
                      <TableRow key={result.runId} data-testid={`row-h0b-result-${result.runId}`}>
                        <TableCell>
                          <Badge variant={classificationVariant(classification)}>
                            {classificationLabel(classification)}
                          </Badge>
                        </TableCell>
                        <TableCell>{result.location}</TableCell>
                        <TableCell>{result.reportDate}</TableCell>
                        <TableCell>{formatTimestamp(result.runAt)}</TableCell>
                        <TableCell className="min-w-80 text-xs text-muted-foreground">
                          {differenceSummary(result.differenceCounts)}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setSelectedRunId(result.runId)}
                            data-testid={`button-h0b-detail-${result.runId}`}
                          >
                            View
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileSearch className="h-5 w-5" />
            Detection Preview
          </CardTitle>
          <CardDescription>
            The same pending-side candidate query used by the active sweep stage. This view performs no claims or runs.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {detectionQuery.isLoading ? (
            <Skeleton className="h-24 w-full" />
          ) : detectionQuery.isError ? (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>Could not load detection preview</AlertTitle>
              <AlertDescription>{String(detectionQuery.error)}</AlertDescription>
            </Alert>
          ) : detection ? (
            <>
              <div className="grid gap-3 text-sm sm:grid-cols-2 lg:grid-cols-4">
                <div><span className="text-muted-foreground">Stage:</span> {detection.state.stage}</div>
                <div><span className="text-muted-foreground">Watermark:</span> {formatTimestamp(detection.state.acceptedAtWatermark)}</div>
                <div><span className="text-muted-foreground">Last run:</span> {formatTimestamp(detection.state.lastRunAt)}</div>
                <div><span className="text-muted-foreground">Reclaim:</span> {detection.state.reclaimTimeoutMinutes} min</div>
              </div>
              <div className="overflow-x-auto rounded-md border">
                <Table data-testid="table-h0b-detection-preview">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Report ID</TableHead>
                      <TableHead>Accepted</TableHead>
                      <TableHead>Location</TableHead>
                      <TableHead>Report date</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {detection.candidates.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} className="py-8 text-center text-muted-foreground">
                          No candidates for the current stage and watermark.
                        </TableCell>
                      </TableRow>
                    ) : detection.candidates.map((candidate) => (
                      <TableRow key={candidate.reportId}>
                        <TableCell className="font-mono text-xs">{candidate.reportId}</TableCell>
                        <TableCell>{formatTimestamp(candidate.acceptedAt)}</TableCell>
                        <TableCell>{candidate.location}</TableCell>
                        <TableCell>{candidate.reportDate}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </>
          ) : null}
        </CardContent>
      </Card>

      {selectedRunId && (
        <Card data-testid="card-h0b-run-detail">
          <CardHeader>
            <CardTitle>Run detail</CardTitle>
            <CardDescription className="font-mono">{selectedRunId}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {detailQuery.isLoading ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading detail
              </div>
            ) : detailQuery.isError ? (
              <Alert variant="destructive">
                <AlertTriangle className="h-4 w-4" />
                <AlertTitle>Could not load run detail</AlertTitle>
                <AlertDescription>{String(detailQuery.error)}</AlertDescription>
              </Alert>
            ) : selected ? (
              <>
                <div className="flex flex-wrap gap-2">
                  <Badge variant={classificationVariant(classify(selected))}>
                    {classificationLabel(classify(selected))}
                  </Badge>
                  <Badge variant="outline">{selected.harnessVersion}</Badge>
                  {selected.laterReportDate && (
                    <Badge variant="secondary">Later report: {selected.laterReportDate}</Badge>
                  )}
                </div>
                <div>
                  <h4 className="mb-2 text-sm font-medium">
                    {selected.outcome === "errored" ? "Error detail" : "Comparison detail"}
                  </h4>
                  <JsonDetail value={selected.outcome === "errored" ? selected.errorDetail : selected.detail} />
                </div>
              </>
            ) : null}
          </CardContent>
        </Card>
      )}
    </div>
  );
}